/**
* Customer Wizard
* Steg-för-steg wizard för att skapa nya CRM-kunder
*/
import { useState } from 'react';
import { useTranslation } from '@/store/languageStore';
import {
CheckCircleIcon,
ArrowLeftIcon,
ArrowRightIcon,
XMarkIcon,
MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useCustomerWizard } from '@/hooks/useCustomerWizard';
import { WIZARD_STEPS, formatPricing } from '@/types/customerWizard';
import CompanySearchModal from '@/components/crm/CompanySearchModal';
import { useAuth } from '@/store/authStore';

export default function CustomerWizard() {
const { t } = useTranslation();
const { user } = useAuth();
const isCrmPartner =
  user?.is_crm_partner === true ||
  user?.is_crm_partner === 1 ||
  user?.is_crm_partner === '1';
const [showCompanySearch, setShowCompanySearch] = useState(false);
const {
currentStep,
data,
isSubmitting,
error,
canProceed,
isLastStep,
updateData,
updateContact,
nextStep,
prevStep,
closeWizard,
handleSubmit,
} = useCustomerWizard();

const handleCompanySelect = (company: {
  organisationsnummer: string;
  foretagsnamn: string;
  adress?: string;
  postnummer?: string;
  postort?: string;
  telefon?: string;
}) => {
  updateData({
    name: company.foretagsnamn,
    org_number: company.organisationsnummer,
    address: company.adress || '',
    postal_code: company.postnummer || '',
    city: company.postort || '',
  });
  setShowCompanySearch(false);
};

const renderStep = () => {
switch (currentStep) {
case 1:
return (
<div className="space-y-6">
<div className="flex items-center justify-between">
<h3 className="text-lg font-semibold text-textDefault">
{t('crm.wizard.basicInfo')}
</h3>
<button
  onClick={() => setShowCompanySearch(true)}
  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-bgPrimary text-white rounded-lg hover:bg-bgPrimaryHover"
>
  <MagnifyingGlassIcon className="w-4 h-4" />
  {t('companySearch.title')}
</button>
</div>
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('crm.wizard.companyName')}
</label>
<input
type="text"
value={data.name}
onChange={(e) => updateData({ name: e.target.value })}
className="w-full px-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
placeholder={t('customerWizard.namePlaceholder')}
/>
</div>
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('crm.wizard.orgNumber')}
</label>
<input
type="text"
value={data.org_number}
onChange={(e) => updateData({ org_number: e.target.value })}
className="w-full px-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
placeholder="XXXXXX-XXXX"
/>
</div>
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('crm.wizard.customerType')}
</label>
<select
value={data.customer_type}
onChange={(e) =>
updateData({ customer_type: e.target.value as any })
}
className="w-full px-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
>
<option value="private">{t('customerWizard.privateCompany')}</option>
<option value="municipality">{t('crm.wizard.municipality')}</option>
<option value="region">{t('crm.wizard.region')}</option>
{!isCrmPartner && <option value="partner">{t('crm.wizard.partner')}</option>}
<option value="car_rental">{t('crm.wizard.carRental')}</option>
</select>
</div>
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('crm.wizard.statusLabel')}
</label>
<div className="flex gap-4">
<label className="flex items-center gap-2 cursor-pointer">
<input
type="radio"
name="status"
value="prospect"
checked={data.status ==='prospect'}
onChange={() => updateData({ status:'prospect' })}
className="w-4 h-4 text-blue-600"
/>
<span className="text-textDefault">
{t('crm.wizard.statusProspect')}
</span>
<span className="text-xs text-textSecondary">
{t('crm.wizard.statusProspectNote')}
</span>
</label>
<label className="flex items-center gap-2 cursor-pointer">
<input
type="radio"
name="status"
value="active"
checked={data.status ==='active'}
onChange={() => updateData({ status:'active' })}
className="w-4 h-4 text-green-600"
/>
<span className="text-textDefault">{t('crm.wizard.statusActive')}</span>
<span className="text-xs text-green-600 font-medium">
{t('crm.wizard.statusActiveNote')}
</span>
</label>
</div>
</div>
</div>
);
case 2:
return (
<div className="space-y-6">
<h3 className="text-lg font-semibold text-textDefault">
{t('crm.wizard.addressContact')}
</h3>
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('crm.wizard.address')}
</label>
<input
type="text"
value={data.address}
onChange={(e) => updateData({ address: e.target.value })}
className="w-full px-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
placeholder="Storgatan 1"
/>
</div>
<div className="grid grid-cols-2 gap-4">
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('crm.wizard.postalCode')}
</label>
<input
type="text"
value={data.postal_code}
onChange={(e) => updateData({ postal_code: e.target.value })}
className="w-full px-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
placeholder="123 45"
/>
</div>
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
Ort
</label>
<input
type="text"
value={data.city}
onChange={(e) => updateData({ city: e.target.value })}
className="w-full px-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
placeholder={t('customerWizard.cityPlaceholder')}
/>
</div>
</div>
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('crm.wizard.website')}
</label>
<input
type="text"
value={data.website}
onChange={(e) => updateData({ website: e.target.value })}
className="w-full px-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
placeholder="https://www.example.se"
/>
</div>
</div>
);
case 3:
return (
<div className="space-y-6">
<h3 className="text-lg font-semibold text-textDefault">
{t('crm.wizard.contactPerson')}
</h3>
<div className="grid grid-cols-2 gap-4">
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('crm.wizard.firstName')}
</label>
<input
type="text"
value={data.contact.first_name}
onChange={(e) =>
updateContact({ first_name: e.target.value })
}
className="w-full px-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
/>
</div>
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('crm.wizard.lastName')}
</label>
<input
type="text"
value={data.contact.last_name}
onChange={(e) => updateContact({ last_name: e.target.value })}
className="w-full px-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
/>
</div>
</div>
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('crm.wizard.email')} <span className="text-red-500">*</span>
</label>
<input
type="email"
value={data.contact.email}
onChange={(e) => updateContact({ email: e.target.value })}
className={`w-full px-4 py-2 border rounded-lg bg-bgBase text-textDefault ${
  !data.contact.email ? 'border-red-400' : 'border-border'
}`}
placeholder="namn@foretag.se"
required
/>
{!data.contact.email && (
  <p className="text-xs text-red-500 mt-1">{t('crm.wizard.emailRequired')}</p>
)}
</div>
<div className="grid grid-cols-2 gap-4">
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('crm.wizard.phone')}
</label>
<input
type="tel"
value={data.contact.phone}
onChange={(e) => updateContact({ phone: e.target.value })}
className="w-full px-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
placeholder="010-123 45 67"
/>
</div>
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('crm.wizard.mobile')}
</label>
<input
type="tel"
value={data.contact.mobile}
onChange={(e) => updateContact({ mobile: e.target.value })}
className="w-full px-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
placeholder="070-123 45 67"
/>
</div>
</div>
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('crm.wizard.role')}
</label>
<select
value={data.contact.role}
onChange={(e) => updateContact({ role: e.target.value })}
className="w-full px-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
>
<option value="decision_maker">{t('crm.wizard.roleDecisionMaker')}</option>
<option value="technical">{t('crm.wizard.roleTechnical')}</option>
<option value="billing">{t('crm.wizard.roleBilling')}</option>
<option value="other">{t('crm.wizard.roleOther')}</option>
</select>
</div>
<div className="border border-border rounded-lg p-3 bg-bgLight">
  <label className="flex items-start gap-2 cursor-pointer">
    <input
      type="checkbox"
      checked={!!data.sendWelcomeEmail}
      onChange={(e) => updateData({ sendWelcomeEmail: e.target.checked })}
      className="w-4 h-4 rounded mt-0.5"
      disabled={!data.contact.email}
    />
    <div>
      <span className="text-sm font-medium text-textDefault">{t('crm.wizard.sendWelcomeEmail')}</span>
      <p className="text-xs text-textSecondary mt-0.5">
        {t('crm.wizard.sendWelcomeEmailDesc')}
      </p>
    </div>
  </label>
</div>
</div>
);
case 4:
return (
<div className="space-y-6">
<h3 className="text-lg font-semibold text-textDefault">
{t('crm.wizard.pricing')}
</h3>
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('crm.wizard.pricingModel')}
</label>
<select
value={data.pricing_model}
onChange={(e) =>
updateData({ pricing_model: e.target.value as any })
}
className="w-full px-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
>
<option value="monthly">{t('customerWizard.fixedMonthly')}</option>
<option value="per_vehicle">{t('crm.wizard.perVehicle')}</option>
<option value="per_user">{t('customerWizard.perUser')}</option>
<option value="custom">{t('crm.wizard.customPricing')}</option>
</select>
</div>
{data.pricing_model ==='monthly' && (
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('crm.wizard.monthlyFee')}
</label>
<input
type="number"
value={data.base_price}
onChange={(e) =>
updateData({ base_price: parseFloat(e.target.value) || 0 })
}
className="w-full px-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
/>
</div>
)}
{data.pricing_model ==='per_vehicle' && (
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('crm.wizard.pricePerVehicle')}
</label>
<input
type="number"
value={data.price_per_vehicle}
onChange={(e) =>
updateData({
price_per_vehicle: parseFloat(e.target.value) || 0,
})
}
className="w-full px-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
/>
</div>
)}
{data.pricing_model ==='per_user' && (
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('crm.wizard.pricePerUser')}
</label>
<input
type="number"
value={data.price_per_user}
onChange={(e) =>
updateData({
price_per_user: parseFloat(e.target.value) || 0,
})
}
className="w-full px-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
/>
</div>
)}
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('crm.wizard.notes')}
</label>
<textarea
value={data.notes}
onChange={(e) => updateData({ notes: e.target.value })}
rows={3}
className="w-full px-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
placeholder={t('customerWizard.otherInfoPlaceholder')}
/>
</div>
</div>
);
case 5:
return (
<div className="space-y-6">
<h3 className="text-lg font-semibold text-textDefault">
{t('crm.wizard.summary')}
</h3>
<div className="bg-bgContainer /50 rounded-lg p-4 space-y-4">
<div>
<h4 className="font-medium text-textDefault">
{data.name}
</h4>
<p className="text-sm text-textSecondary">
{data.org_number || t('crm.wizard.noOrgNumber')} • {data.customer_type}
</p>
</div>
<div className="border-t border-border pt-4">
<p className="text-sm">
<span className="text-textSecondary">{t('crm.wizard.statusLabel')}</span>{''}
<span
className={
data.status ==='active'
?'text-green-600 font-medium'
:'text-yellow-600'
}
>
{data.status ==='active'
? t('crm.wizard.statusActiveLabel')
: t('crm.wizard.statusProspect')}
</span>
</p>
</div>
{data.address && (
<div className="border-t border-border pt-4">
<p className="text-sm text-textSecondary">
{data.address}
<br />
{data.postal_code} {data.city}
</p>
</div>
)}
{data.contact.first_name && (
<div className="border-t border-border pt-4">
<p className="text-sm">
<span className="font-medium">
{data.contact.first_name} {data.contact.last_name}
</span>
<br />
<span className="text-textSecondary">{data.contact.email}</span>
</p>
</div>
)}
<div className="border-t border-border pt-4">
<p className="text-sm">
<span className="text-textSecondary">{t('crm.wizard.pricingLabel')}:</span>{''}
{formatPricing(data)}
</p>
</div>
</div>
{error && (
<div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg">
{error}
</div>
)}
</div>
);
}
};
return (
<div className="min-h-screen bg-bgBase py-8">
<div className="max-w-3xl mx-auto px-4">
{/* Header */}
<div className="flex items-center justify-between mb-8">
<h1 className="text-2xl font-bold text-textDefault">
{t('crm.createCustomer') ||'Skapa ny kund'}
</h1>
<button
onClick={closeWizard}
className="p-2 text-textSecondary hover:text-textDefault"
>
<XMarkIcon className="w-6 h-6"/>
</button>
</div>
{/* Steps */}
<div className="mb-8">
<div className="flex items-center justify-between">
{WIZARD_STEPS.map((step, index) => (
<div key={step.id} className="flex items-center">
<div
className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
currentStep >= step.id
?'bg-blue-600 border-blue-600 text-white'
:'border-border text-textTertiary'
}`}
>
{currentStep > step.id ? (
<CheckCircleIcon className="w-6 h-6"/>
) : (
<step.icon className="w-5 h-5"/>
)}
</div>
{index < WIZARD_STEPS.length - 1 && (
<div
className={`w-16 h-0.5 mx-2 ${
currentStep > step.id
?'bg-blue-600'
:'bg-bgBase'
}`}
/>
)}
</div>
))}
</div>
<div className="flex justify-between mt-2">
{WIZARD_STEPS.map((step) => (
<span
key={step.id}
className={`text-xs ${
currentStep === step.id
?'text-blue-600 font-medium'
:'text-textSecondary'
}`}
>
{step.name}
</span>
))}
</div>
</div>
{/* Content */}
<div className="bg-bgElevated rounded-xl shadow-lg p-8">
{renderStep()}
{/* Navigation */}
<div className="flex justify-between mt-8 pt-6 border-t border-border">
<button
onClick={prevStep}
disabled={currentStep === 1}
className="flex items-center gap-2 px-4 py-2 text-textSecondary hover:text-textDefault disabled:opacity-50"
>
<ArrowLeftIcon className="w-4 h-4"/>
{t('crm.wizard.back')}
</button>
{!isLastStep ? (
<button
onClick={nextStep}
disabled={!canProceed}
className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
>
{t('crm.wizard.next')}
<ArrowRightIcon className="w-4 h-4"/>
</button>
) : (
<button
onClick={handleSubmit}
disabled={isSubmitting || !canProceed}
className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
>
{isSubmitting ? t('crm.wizard.creating') : t('crm.wizard.createCustomer')}
<CheckCircleIcon className="w-5 h-5"/>
</button>
)}
</div>
</div>
</div>

{showCompanySearch && (
  <CompanySearchModal
    onSelect={handleCompanySelect}
    onClose={() => setShowCompanySearch(false)}
  />
)}
</div>
);
}
